import React from "react";

import {
  AgentSubtask,
  AgentTask,
  AgentTaskStatus,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import {
  CheckCircleIcon,
  ChevronRightIcon,
  ExpandMoreIcon,
  RadioButtonCheckedIcon,
  RadioButtonUncheckedIcon,
} from "@/shared/foundation/icons/mui";

import styles from "./TaskPanel.module.css";

/**
 * How the panel is laid out, decided by the chat's width (see
 * `useIsWideLayout` in AgentThreadByIdView):
 * - `tight`: a docked-sidebar strip. A live view of open work — in-progress and
 *   pending tasks only; completed ones are hidden.
 * - `wide`: a table of contents beside the chat. Every task as a title,
 *   collapsed by default and expandable per row to reveal sub-tasks/conclusion.
 */
export type TaskPanelLayout = "tight" | "wide";

interface TaskPanelProps {
  tasks: AgentTask[];
  layout: TaskPanelLayout;
  /** Task whose chat section the reader is currently looking at (scroll-spy). */
  activeTaskId?: number;
  /** Jump the conversation to a task's section. The panel only invokes this for
   *  clickable rows (tasks with a started region). */
  onSelectTask: (taskId: number) => void;
}

/** A task is clickable once it has started a region in the transcript to jump
 *  to; pending tasks own no conversation yet. */
function isClickable(task: AgentTask): boolean {
  return Boolean(task.start);
}

function statusIcon(status: AgentTaskStatus): React.ReactElement {
  switch (status) {
    case AgentTaskStatus.COMPLETED:
      return (
        <CheckCircleIcon className={`${styles.icon} ${styles.iconCompleted}`} />
      );
    case AgentTaskStatus.IN_PROGRESS:
      return (
        <RadioButtonCheckedIcon
          className={`${styles.icon} ${styles.iconActive}`}
        />
      );
    case AgentTaskStatus.PENDING:
    default:
      return (
        <RadioButtonUncheckedIcon
          className={`${styles.icon} ${styles.iconPending}`}
        />
      );
  }
}

/** A sub-task, rendered as a plain bullet point. Sub-task status is not
 *  surfaced here — the backend never flips it to done in practice, so a
 *  checkbox would sit permanently unchecked and read as incomplete work. */
function Subtask({ subtask }: { subtask: AgentSubtask }) {
  return <li className={styles.subtask}>{subtask.title}</li>;
}

function SubtaskList({ subtasks }: { subtasks: AgentSubtask[] }) {
  if (subtasks.length === 0) {
    return null;
  }
  return (
    <ul className={styles.subtaskList}>
      {subtasks.map((subtask, index) => (
        // Sub-tasks are append-only in the backend contract (add_subtasks
        // appends; completion flips status by index) — never reordered or
        // removed — so the array index is a stable key. Titles are not unique,
        // so keying by title risks collisions that mis-reconcile rows.
        <Subtask key={index} subtask={subtask} />
      ))}
    </ul>
  );
}

interface TaskRowProps {
  task: AgentTask;
  /** Reader is currently looking at this task's section (scroll-spy). */
  active: boolean;
  /** Reveal sub-tasks and (when completed) the conclusion below the title. */
  showDetail: boolean;
  onSelect: (taskId: number) => void;
  /** Expand/collapse control rendered before the status icon. A `<button>` in
   *  the wide TOC, a fixed-width spacer for rows with no detail, or omitted
   *  entirely in the tight strip. */
  expandControl?: React.ReactNode;
}

/** One task: optional expand control, status icon, title (a jump button when
 *  clickable), and — when expanded — its sub-tasks and conclusion. */
function TaskRow({
  task,
  active,
  showDetail,
  onSelect,
  expandControl,
}: TaskRowProps) {
  const isActive = task.status === AgentTaskStatus.IN_PROGRESS;
  const clickable = isClickable(task);

  return (
    <li className={styles.taskItem}>
      <div className={active ? styles.taskRowActive : styles.taskRow}>
        {expandControl}
        {statusIcon(task.status)}
        {clickable ? (
          <button
            type="button"
            className={isActive ? styles.titleButtonActive : styles.titleButton}
            onClick={() => onSelect(task.task_id)}
            title="Jump to this task in the conversation"
          >
            {task.title}
          </button>
        ) : (
          <span className={isActive ? styles.titleActive : styles.title}>
            {task.title}
          </span>
        )}
      </div>
      {showDetail && (
        <>
          {task.status === AgentTaskStatus.COMPLETED && task.conclusion && (
            <p className={styles.conclusion}>{task.conclusion}</p>
          )}
          <SubtaskList subtasks={task.subtasks} />
        </>
      )}
    </li>
  );
}

function PanelHeader({
  collapsed,
  onToggleCollapsed,
  completed,
  open,
  trailing,
}: {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  completed: number;
  open: number;
  /** Optional control rendered at the end of the header row (e.g. the tight
   *  strip's "View all" toggle). */
  trailing?: React.ReactNode;
}) {
  return (
    <div className={styles.header}>
      <button
        type="button"
        className={styles.collapseToggle}
        aria-expanded={!collapsed}
        aria-label={collapsed ? "Expand tasks" : "Collapse tasks"}
        onClick={onToggleCollapsed}
      >
        {collapsed ? (
          <ChevronRightIcon className={styles.chevron} />
        ) : (
          <ExpandMoreIcon className={styles.chevron} />
        )}
        <span className={styles.headerLabel}>Tasks</span>
        <span className={styles.count}>
          {`${completed} completed, ${open} open`}
        </span>
      </button>
      {trailing}
    </div>
  );
}

/** Tight, docked-sidebar strip: a live view of open work — in-progress and
 *  pending tasks, with the active task's sub-tasks shown inline. Completed
 *  tasks are hidden by default but revealable via "View all" (the only way to
 *  reach them here, e.g. once every task is done). */
function TightView({
  tasks,
  activeTaskId,
  onSelectTask,
  completed,
  open,
}: {
  tasks: AgentTask[];
  activeTaskId?: number;
  onSelectTask: (taskId: number) => void;
  completed: number;
  open: number;
}) {
  const [collapsed, setCollapsed] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);

  const visibleTasks = showAll
    ? tasks
    : tasks.filter((task) => task.status !== AgentTaskStatus.COMPLETED);

  // Only offer the toggle when there's something hidden to reveal.
  const viewAllToggle =
    !collapsed && completed > 0 ? (
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={showAll}
        onClick={() => setShowAll((prev) => !prev)}
      >
        {showAll ? "Show less" : "View all"}
      </button>
    ) : undefined;

  return (
    <section className={styles.container} aria-label="Task list">
      <PanelHeader
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((prev) => !prev)}
        completed={completed}
        open={open}
        trailing={viewAllToggle}
      />
      {!collapsed && visibleTasks.length > 0 && (
        <ul className={styles.taskList}>
          {visibleTasks.map((task) => (
            <TaskRow
              key={task.task_id}
              task={task}
              active={task.task_id === activeTaskId}
              // Active task shows its sub-tasks inline; completed tasks show
              // their conclusion/sub-tasks only once "View all" is on.
              showDetail={
                task.status === AgentTaskStatus.IN_PROGRESS ||
                (showAll && task.status === AgentTaskStatus.COMPLETED)
              }
              onSelect={onSelectTask}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Wide table of contents beside the chat: every task as a title, collapsed by
 *  default, each row expandable to reveal its sub-tasks and conclusion. */
function WideView({
  tasks,
  activeTaskId,
  onSelectTask,
  completed,
  open,
}: {
  tasks: AgentTask[];
  activeTaskId?: number;
  onSelectTask: (taskId: number) => void;
  completed: number;
  open: number;
}) {
  const [collapsed, setCollapsed] = React.useState(false);
  const [expandedIds, setExpandedIds] = React.useState<ReadonlySet<number>>(
    () => new Set(),
  );

  const toggleRow = (taskId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  return (
    <section className={styles.container} aria-label="Task list">
      <PanelHeader
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((prev) => !prev)}
        completed={completed}
        open={open}
      />
      {!collapsed && (
        <ul className={styles.taskList}>
          {tasks.map((task) => {
            const isInProgress = task.status === AgentTaskStatus.IN_PROGRESS;
            const isExpanded = expandedIds.has(task.task_id);
            // The live task keeps its sub-tasks visible without a click (as in
            // the tight strip); every other row reveals detail on demand.
            const showDetail = isInProgress || isExpanded;
            const hasDetail =
              task.subtasks.length > 0 ||
              (task.status === AgentTaskStatus.COMPLETED && !!task.conclusion);
            const showToggle = hasDetail && !isInProgress;
            return (
              <TaskRow
                key={task.task_id}
                task={task}
                active={task.task_id === activeTaskId}
                showDetail={showDetail}
                onSelect={onSelectTask}
                expandControl={
                  showToggle ? (
                    <button
                      type="button"
                      className={styles.rowChevron}
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded ? "Collapse details" : "Expand details"
                      }
                      onClick={() => toggleRow(task.task_id)}
                    >
                      {isExpanded ? (
                        <ExpandMoreIcon className={styles.chevron} />
                      ) : (
                        <ChevronRightIcon className={styles.chevron} />
                      )}
                    </button>
                  ) : (
                    // Keep titles aligned with rows that have a chevron (rows
                    // with no detail, and the always-open live task).
                    <span
                      className={styles.rowChevronSpacer}
                      aria-hidden="true"
                    />
                  )
                }
              />
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * A live panel summarizing the agent's task list for the thread, doubling as a
 * table of contents for the conversation. Layout-dependent (see
 * {@link TaskPanelLayout}): a tight strip of open work when the chat is narrow,
 * a full clickable TOC when it is wide. Task titles jump to their section in
 * the transcript; the row matching the reader's scroll position is highlighted.
 * Renders nothing when there are no tasks, and updates as the thread's delta
 * poll merges new state.
 */
export function TaskPanel({
  tasks,
  layout,
  activeTaskId,
  onSelectTask,
}: TaskPanelProps) {
  if (tasks.length === 0) {
    return null;
  }

  const completed = tasks.filter(
    (task) => task.status === AgentTaskStatus.COMPLETED,
  ).length;
  const open = tasks.length - completed;

  return layout === "wide" ? (
    <WideView
      tasks={tasks}
      activeTaskId={activeTaskId}
      onSelectTask={onSelectTask}
      completed={completed}
      open={open}
    />
  ) : (
    <TightView
      tasks={tasks}
      activeTaskId={activeTaskId}
      onSelectTask={onSelectTask}
      completed={completed}
      open={open}
    />
  );
}
